import {
  ApiError,
  type ApiErrorBody,
  type AuthStatus,
  type Book,
  type BookStatus,
  type BooksListResponse,
  type Category,
  type ChatMessage,
  type ChatResponse,
  type CreateBookOptions,
  type CreateBookRequest,
  type EventsResponse,
  type HealthResponse,
  type OutputFormat,
  type Settings,
  type SettingsUpdateRequest,
} from "./types";
import {
  appCheckConfigured,
  currentAppCheckToken,
  currentIdToken,
  firebaseConfigured,
} from "../lib/firebase";

/** Fired on window whenever any API call receives a 401. App.tsx listens and shows the login screen. */
export const UNAUTHORIZED_EVENT = "boort:unauthorized";

export function onUnauthorized(handler: () => void): () => void {
  window.addEventListener(UNAUTHORIZED_EVENT, handler);
  return () => window.removeEventListener(UNAUTHORIZED_EVENT, handler);
}

interface RequestOptions extends RequestInit {
  /** Skip dispatching UNAUTHORIZED_EVENT on a 401 — used by the login call itself. */
  skipAuthRedirect?: boolean;
}

async function apiFetch(path: string, init: RequestOptions = {}): Promise<Response> {
  const { skipAuthRedirect, headers, ...rest } = init;
  // Firebase mode: every call carries a fresh ID token (the SDK refreshes it),
  // plus an App Check attestation where one is available.
  const idToken = firebaseConfigured ? await currentIdToken() : null;
  const appCheckToken = appCheckConfigured ? await currentAppCheckToken() : null;
  const res = await fetch(path, {
    ...rest,
    credentials: "include",
    headers: {
      Accept: "application/json",
      ...(rest.body ? { "Content-Type": "application/json" } : {}),
      ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
      ...(appCheckToken ? { "X-Firebase-AppCheck": appCheckToken } : {}),
      ...headers,
    },
  });

  if (res.status === 401 && !skipAuthRedirect) {
    window.dispatchEvent(new Event(UNAUTHORIZED_EVENT));
  }

  if (!res.ok) {
    let code = "INTERNAL";
    let message = res.statusText || "Request failed";
    let hint: string | null = null;
    try {
      const body = (await res.clone().json()) as ApiErrorBody;
      if (body && body.error) {
        code = body.error.code ?? code;
        message = body.error.message ?? message;
        hint = body.error.hint ?? null;
      }
    } catch {
      // Response body wasn't JSON (or empty) — keep the defaults above.
    }
    throw new ApiError(res.status, code, message, hint);
  }

  return res;
}

async function apiJson<T>(path: string, init?: RequestOptions): Promise<T> {
  const res = await apiFetch(path, init);
  if (res.status === 204) {
    return undefined as T;
  }
  return (await res.json()) as T;
}

// ---------------------------------------------------------------------------
// Books
// ---------------------------------------------------------------------------

export interface CreateBookResult {
  book: Book;
  /** true when the server created a new job (202); false when it returned an existing duplicate (200). */
  created: boolean;
}

export async function createBook(url: string, options?: CreateBookOptions): Promise<CreateBookResult> {
  const payload: CreateBookRequest = { url, options };
  const res = await apiFetch("/api/books", { method: "POST", body: JSON.stringify(payload) });
  const book = (await res.json()) as Book;
  return { book, created: res.status === 202 };
}

export interface ListBooksParams {
  status?: BookStatus;
  q?: string;
  limit?: number;
  offset?: number;
  /** Category id to filter by, or the literal "none" for uncategorized books. */
  category?: string;
}

export function listBooks(params: ListBooksParams = {}): Promise<BooksListResponse> {
  const search = new URLSearchParams();
  if (params.status) search.set("status", params.status);
  if (params.q) search.set("q", params.q);
  if (params.limit != null) search.set("limit", String(params.limit));
  if (params.offset != null) search.set("offset", String(params.offset));
  if (params.category) search.set("category", params.category);
  const qs = search.toString();
  return apiJson<BooksListResponse>(`/api/books${qs ? `?${qs}` : ""}`);
}

export function getBook(id: string): Promise<Book> {
  return apiJson<Book>(`/api/books/${id}`);
}

export function getBookEvents(id: string, afterId?: number): Promise<EventsResponse> {
  const qs = afterId != null ? `?after_id=${afterId}` : "";
  return apiJson<EventsResponse>(`/api/books/${id}/events${qs}`);
}

export async function getBookContent(id: string): Promise<string> {
  const res = await apiFetch(`/api/books/${id}/content`);
  return res.text();
}

export async function getBookSummary(id: string): Promise<string> {
  const res = await apiFetch(`/api/books/${id}/summary`);
  return res.text();
}

export function retryBook(id: string): Promise<Book> {
  return apiJson<Book>(`/api/books/${id}/retry`, { method: "POST" });
}

export function regenerateBook(id: string, options?: CreateBookOptions): Promise<Book> {
  return apiJson<Book>(`/api/books/${id}/regenerate`, {
    method: "POST",
    body: JSON.stringify({ options }),
  });
}

export function cancelBook(id: string): Promise<Book> {
  return apiJson<Book>(`/api/books/${id}/cancel`, { method: "POST" });
}

export async function deleteBook(id: string): Promise<void> {
  await apiJson<void>(`/api/books/${id}`, { method: "DELETE" });
}

export function fileUrl(id: string, fmt: OutputFormat): string {
  return `/api/books/${id}/files/${fmt}`;
}

/**
 * Downloads go through fetch rather than a plain link: a browser navigation
 * carries no Authorization header, so the API refuses it. The response is
 * saved from a blob instead, which keeps the credential out of the URL.
 */
export async function downloadBookFile(id: string, fmt: OutputFormat): Promise<void> {
  const response = await apiFetch(fileUrl(id, fmt));
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filenameFrom(response.headers.get("Content-Disposition")) ?? `boort.${fmt}`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoking immediately can cancel the save in some browsers.
  window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/** The server's chosen name, which carries the book title. */
export function filenameFrom(disposition: string | null): string | null {
  if (!disposition) return null;
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  if (encoded) {
    try {
      return decodeURIComponent(encoded[1]);
    } catch {
      // fall through to the plain form
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(disposition);
  return plain ? plain[1] : null;
}

export function coverUrl(id: string): string {
  return `/api/books/${id}/cover`;
}

/**
 * An <img> cannot send headers either, so a cover served by the API is
 * fetched and handed to the tag as an object URL. External thumbnails need
 * none of this and are returned unchanged.
 */
export async function imageObjectUrl(url: string): Promise<string> {
  if (!url.startsWith("/api/")) return url;
  const response = await apiFetch(url);
  return URL.createObjectURL(await response.blob());
}

export function streamUrl(id: string): string {
  return `/api/books/${id}/stream`;
}

/** EventSource cannot send headers; in Firebase mode the ID token rides as a
 * query parameter the backend accepts for exactly this purpose. */
export async function authedStreamUrl(id: string): Promise<string> {
  const base = streamUrl(id);
  if (!firebaseConfigured) return base;
  const token = await currentIdToken();
  return token ? `${base}?access_token=${encodeURIComponent(token)}` : base;
}

export function updateBookCategory(id: string, categoryId: string | null): Promise<Book> {
  return apiJson<Book>(`/api/books/${id}/category`, {
    method: "PUT",
    body: JSON.stringify({ category_id: categoryId }),
  });
}

// ---------------------------------------------------------------------------
// Categories
// ---------------------------------------------------------------------------

export interface CategoriesListResponse {
  categories: Category[];
}

export function listCategories(): Promise<CategoriesListResponse> {
  return apiJson<CategoriesListResponse>("/api/categories");
}

export interface CreateCategoryRequest {
  name: string;
  chat_prompt?: string;
}

export function createCategory(payload: CreateCategoryRequest): Promise<Category> {
  return apiJson<Category>("/api/categories", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export interface UpdateCategoryRequest {
  name?: string;
  chat_prompt?: string;
}

export function updateCategory(id: string, payload: UpdateCategoryRequest): Promise<Category> {
  return apiJson<Category>(`/api/categories/${id}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export async function deleteCategory(id: string): Promise<void> {
  await apiJson<void>(`/api/categories/${id}`, { method: "DELETE" });
}

// ---------------------------------------------------------------------------
// Chat
// ---------------------------------------------------------------------------

export interface ChatRequest {
  /** An explicit selection of books; takes precedence over category_id. */
  book_ids?: string[];
  category_id: string | null;
  messages: ChatMessage[];
}

export function sendChat(payload: ChatRequest): Promise<ChatResponse> {
  return apiJson<ChatResponse>("/api/chat", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

// ---------------------------------------------------------------------------
// Settings & health
// ---------------------------------------------------------------------------

export function getSettings(): Promise<Settings> {
  return apiJson<Settings>("/api/settings");
}

export function updateSettings(payload: SettingsUpdateRequest): Promise<Settings> {
  return apiJson<Settings>("/api/settings", {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export function getHealth(): Promise<HealthResponse> {
  return apiJson<HealthResponse>("/api/health");
}

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------

export async function login(password: string): Promise<void> {
  await apiJson<void>("/api/auth/login", {
    method: "POST",
    body: JSON.stringify({ password }),
    skipAuthRedirect: true,
  });
}

export function getAuthStatus(): Promise<AuthStatus> {
  return apiJson<AuthStatus>("/api/auth/status", { skipAuthRedirect: true });
}

export { ApiError };
